import type { Bundle } from '../types'

/** Administration : utilisateurs et organisations. */
export const admin = {
  // Utilisateurs
  'users.title': { fr: 'Gestion des utilisateurs', en: 'User management' },
  'users.allOrgs': { fr: 'Toutes les organisations', en: 'All organisations' },
  'users.newUser': { fr: '+ Nouvel utilisateur', en: '+ New user' },
  'users.formEdit': { fr: "Modifier l'utilisateur", en: 'Edit user' },
  'users.formNew': { fr: 'Nouvel utilisateur', en: 'New user' },
  'users.fullName': { fr: 'Nom complet', en: 'Full name' },
  'users.passwordUnchanged': { fr: 'Mot de passe (vide = inchangé)', en: 'Password (empty = unchanged)' },
  'users.passwordMin': {
    fr: 'Mot de passe ({n} caractères minimum)',
    en: 'Password ({n} characters minimum)',
  },
  'users.passwordRequired': { fr: 'Mot de passe requis', en: 'Password required' },
  'users.loadError': { fr: 'Erreur de chargement', en: 'Could not load data' },
  'users.superadminNote': {
    fr: "Un super admin administre la plateforme : il n'est soumis ni au cloisonnement par pays ni aux modules d'une organisation, et voit donc tous les clients. À réserver à un compte d'administration, distinct d'un compte de travail quotidien.",
    en: 'A super admin administers the platform: neither country partitioning nor an organisation’s modules apply, so they see every customer. Keep it for an administration account, separate from a day-to-day working account.',
  },
  'users.allowedCountries': { fr: 'Pays autorisés', en: 'Authorised countries' },
  'users.allowedCountriesHint': { fr: 'Vide = tous les pays', en: 'Empty = every country' },
  'users.allowedTabs': { fr: 'Onglets autorisés', en: 'Authorised screens' },
  'users.allowedTabsHint': { fr: 'Vide = tous les onglets', en: 'Empty = every screen' },
  'users.colPermissions': { fr: 'Permissions', en: 'Permissions' },
  'users.fullAccess': { fr: 'Accès complet', en: 'Full access' },
  'users.allCountriesTabs': { fr: 'Tous pays / onglets', en: 'All countries / screens' },
  'users.tabCount': { fr: '{n} onglet(s)', en: '{n} screen(s)' },
  'users.active': { fr: 'Actif', en: 'Active' },
  'users.disabled': { fr: 'Désactivé', en: 'Disabled' },
  'users.deactivate': { fr: 'Désactiver', en: 'Deactivate' },
  'users.activate': { fr: 'Activer', en: 'Activate' },
  'users.empty': { fr: 'Aucun utilisateur', en: 'No user' },
  'users.deleteConfirm': { fr: 'Supprimer {name} ?', en: 'Delete {name}?' },
  'users.editAria': { fr: 'Modifier {name}', en: 'Edit {name}' },
  'users.deleteAria': { fr: 'Supprimer {name}', en: 'Delete {name}' },
  'users.toggleAria': { fr: '{action} {name}', en: '{action} {name}' },
  'users.genericError': { fr: 'Erreur', en: 'Error' },

  // Modules (libellés partagés par Utilisateurs et Organisations)
  'module.merchants': { fr: 'Marchands', en: 'Merchants' },
  'module.discovery': { fr: 'Usurpation de marques', en: 'Brand impersonation' },
  'module.webscan': { fr: 'Marchands non déclarés', en: 'Undeclared merchants' },
  'module.ads': { fr: 'Détection pubs', en: 'Ad detection' },
  'module.betting': { fr: 'Paris & jeux', en: 'Betting & gambling' },
  'module.brands': { fr: 'Modèles de recherche', en: 'Search templates' },
  'module.rules': { fr: 'Règles pays', en: 'Country rules' },
  'module.users': { fr: 'Utilisateurs', en: 'Users' },
  'module.orgs': { fr: 'Organisations', en: 'Organisations' },

  // Organisations
  'orgs.title': { fr: 'Gestion des organisations', en: 'Organisation management' },
  'orgs.newOrg': { fr: '+ Nouvelle organisation', en: '+ New organisation' },
  'orgs.formEdit': { fr: "Modifier l'organisation", en: 'Edit organisation' },
  'orgs.formNew': { fr: 'Nouvelle organisation', en: 'New organisation' },
  'orgs.loadError': {
    fr: 'Erreur de chargement des organisations',
    en: 'Could not load the organisations',
  },
  'orgs.slug': { fr: 'Identifiant court', en: 'Slug' },
  'orgs.slugTitle': {
    fr: 'Lettres minuscules, chiffres et tirets uniquement',
    en: 'Lower-case letters, digits and hyphens only',
  },
  'orgs.contactEmail': { fr: 'Courriel de contact', en: 'Contact email' },
  'orgs.contactName': { fr: 'Nom du contact', en: 'Contact name' },
  'orgs.plan': { fr: 'Formule', en: 'Plan' },
  'orgs.maxUsers': { fr: 'Utilisateurs maximum', en: 'Maximum users' },
  'orgs.maxCountries': { fr: 'Pays maximum', en: 'Maximum countries' },
  'orgs.assignedCountries': { fr: 'Pays assignés', en: 'Assigned countries' },
  'orgs.countriesCount': { fr: '{n} / {max} pays', en: '{n} / {max} countries' },
  'orgs.openModules': { fr: 'Modules ouverts', en: 'Modules enabled' },
  'orgs.modulesCount': { fr: '{n} / {max} modules', en: '{n} / {max} modules' },
  // Phrase coupée autour du mot mis en gras : une balise écrite dans le
  // catalogue s'afficherait telle quelle.
  'orgs.modulesNoteA': {
    fr: "L'organisation consulte un balayage si elle a le pays",
    en: 'An organisation sees a sweep when it has the country',
  },
  'orgs.modulesNoteAnd': { fr: 'et', en: 'and' },
  'orgs.modulesNoteB': {
    fr: 'le module. Aucun module coché : elle ne voit rien.',
    en: 'the module. With no module ticked, it sees nothing.',
  },
  'orgs.requiredFields': {
    fr: 'Nom, identifiant court et courriel de contact sont requis',
    en: 'Name, slug and contact email are required',
  },
  'orgs.moduleRequired': {
    fr: 'Au moins un module est requis : une organisation sans module ne voit rien.',
    en: 'At least one module is required: an organisation with none sees nothing.',
  },
  'orgs.countryRequired': { fr: 'Au moins un pays est requis', en: 'At least one country is required' },
  'orgs.colLimits': { fr: 'Limites', en: 'Limits' },
  'orgs.colContact': { fr: 'Contact', en: 'Contact' },
  'orgs.limits': { fr: '{users} utilisateurs / {countries} pays', en: '{users} users / {countries} countries' },
  'orgs.noModules': { fr: 'Aucun — ne voit rien', en: 'None — sees nothing' },
  'orgs.inactive': { fr: 'Inactif', en: 'Inactive' },
  'orgs.empty': { fr: 'Aucune organisation', en: 'No organisation' },
  'orgs.deleteConfirm': { fr: "Supprimer l'organisation « {name} » ?", en: 'Delete the organisation “{name}”?' },
  'orgs.statsLoading': { fr: 'Chargement des statistiques…', en: 'Loading statistics…' },
  'orgs.statUsers': { fr: 'Utilisateurs', en: 'Users' },
  'orgs.statMerchants': { fr: 'Marchands', en: 'Merchants' },
  'orgs.statAdvertisers': { fr: 'Annonceurs', en: 'Advertisers' },
  'orgs.statReports': { fr: 'Rapports de crawl', en: 'Crawl reports' },
  'orgs.statScans': { fr: 'Scans effectués', en: 'Scans run' },
} satisfies Bundle
